import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {getValidToken} from '../utils/auth';
import Sidebar from '../components/Sidebar';

const RoadmapPage = () => {
  // State for roadmap data, loading, and error
  const [roadmapData, setRoadmapData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State to track the selected item and modal visibility
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const navigate = useNavigate();

  // Fetch roadmap data from API when component mounts
  useEffect(() => {
    const token = getValidToken();
    if (!token) {
      navigate('/manager/login');
    } else {
      fetchRoadmap();
    }
  }, [navigate]);

  // Function to fetch roadmap data from the API
  const fetchRoadmap = async () => {
    const token = getValidToken();
    if (!token) {
      navigate('/manager/login');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/roadmap', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRoadmapData(data);
      } else if (response.status === 401) {
        // Token expired or invalid
        navigate('/manager/login');
      } else {
        const errorData = await response.json();
        setError(`Błąd: ${errorData.message || 'Nie udało się pobrać danych roadmapy'}`);
      }
    } catch (error) {
      setError('Wystąpił błąd podczas pobierania danych roadmapy');
      console.error('Błąd:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Open modal with item details
  const openItemDetails = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Get color class based on status
  const getStatusColor = (status) => {
    switch(status) {
      case 'DONE': return 'border-green-400';
      case 'IN_PROGRESS': return 'border-yellow-400';
      case 'PLANNED': return 'border-gray-400';
      default: return 'border-gray-400';
    }
  };

  // Render a single roadmap item
  const renderRoadmapItem = (item, index) => (
    <li 
      key={`${item.title}-${index}`} 
      className={`border-l-4 ${getStatusColor(item.status)} pl-3 py-2 mb-3 cursor-pointer hover:bg-gray-50 transition-colors duration-200`}
      onClick={() => openItemDetails(item)}
    >
      <div className="flex justify-between items-center">
        <div>
          <span className="font-medium">{item.title}</span>
          <p className="text-sm text-gray-600 mt-1">{item.shortDescription}</p>
        </div>
        <span className="text-gray-400 text-sm">→</span>
      </div>
    </li>
  );

  // Modal component
  const Modal = () => {
    if (!selectedItem || !isModalOpen) return null;

    const getStatusLabel = (status) => {
      switch(status) {
        case 'DONE': return 'Ukończone';
        case 'IN_PROGRESS': return 'W trakcie';
        case 'PLANNED': return 'Planowane';
        default: return 'Nieznany';
      }
    };

    const getStatusBgColor = (status) => {
      switch(status) {
        case 'DONE': return 'bg-green-100 text-green-800';
        case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
        case 'PLANNED': return 'bg-gray-100 text-gray-800';
        default: return 'bg-gray-100 text-gray-800';
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold">{selectedItem.title}</h3>
                <span className={`inline-block mt-2 px-3 py-1 text-sm font-medium rounded-full ${getStatusBgColor(selectedItem.status)}`}>
                  {getStatusLabel(selectedItem.status)}
                </span>
              </div>
              <button 
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                ✕
              </button>
            </div>
            <p className="font-medium text-gray-700 mb-3">{selectedItem.shortDescription}</p>
            <div className="border-t pt-3">
              <p className="text-gray-800 whitespace-pre-line text-justify">{selectedItem.description}</p>
            </div>
          </div>
          <div className="bg-gray-100 px-6 py-3 flex justify-end rounded-b-lg">
            <button 
              onClick={closeModal}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Loading indicator
  if (isLoading) {
    return (
      <Sidebar>
        <div className="max-w-4xl mx-auto flex justify-center items-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </Sidebar>
    );
  }

  // Error message
  if (error) {
    return (
      <Sidebar>
        <div className="max-w-4xl mx-auto mt-8">
          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
            <p className="text-red-700">{error}</p>
            <button 
              onClick={fetchRoadmap}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-6">
          <h1 className="text-3xl font-bold">Roadmap</h1>
        </div>
        
        <div className="space-y-10">
          {roadmapData.map((versionGroup) => (
            <div key={versionGroup.version} className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold mb-4 border-b pb-2">
                Wersja {versionGroup.version}
              </h2>
              <ul className="space-y-2">
                {versionGroup.roadmapItems.map((item, index) => renderRoadmapItem(item, index))}
              </ul>
            </div>
          ))}
        </div>

        {/* Modal for item details */}
        <Modal />
      </div>
    </Sidebar>
  );
};

export default RoadmapPage; 